import React, { useState, useCallback, useMemo } from 'react';
import { Col, FormGroup, Label, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { Field } from 'formik';
import AsyncSelect from 'react-select/async';
import { get } from 'lodash';

const CUSTOM_STYLES = {
  control: (base) => ({
    ...base,
    borderRadius: '30px',
    border: `2px solid #c7c5db`,
    padding: '0.5rem 1rem',
    height: 'auto',
    backgroundColor: '#fff',
    color: '#050220',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#c7c5db',
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: '#050220',
    opacity: 0.5,
  }),
};

const THEME_CONFIG = (theme) => ({
  ...theme,
  margin: 0,
});

function FormAsyncSelectFieldBorderedInner(props) {
  const {
    label,
    placeholder,
    form,
    field,
    onChangeHandler = () => {},
    loadOptions,
    defaultOptions,
    onInputChange = () => {},
    loadRightAway,
    invalid,
    disabled,
    required,
  } = props;

  const [isActiveFetchData, setActiveFetchData] = useState(loadRightAway);

  const error = form.errors[field.name];
  const touched = form.touched[field.name];
  const value = get(form.values, field.name, '');

  const onFocusHandler = useCallback(() => {
    setActiveFetchData(true);
  }, []);

  const loadOneOption = useCallback(
    (inputValue, callback) => {
      if (!value?.label) {
        callback(false);
        return false;
      }
      callback([value]);
      return true;
    },
    [value],
  );

  const handleChange = useCallback(
    (option) => {
      form.setFieldValue(field.name, option);
      onChangeHandler(option);
    },
    [form, field.name, onChangeHandler],
  );

  const handleBlur = useCallback(() => {
    form.setFieldTouched(field.name, true);
  }, [form, field.name]);

  const selectClassName = useMemo(() => {
    const baseClass = 'react-select-container';
    const errorClass = invalid || (touched && error) ? 'react-select__control-error' : '';
    return `${errorClass} ${baseClass}`.trim();
  }, [invalid, error, touched]);
  const selectElement = (
    <AsyncSelect
      styles={CUSTOM_STYLES}
      loadOptions={isActiveFetchData ? loadOptions : loadOneOption}
      defaultOptions={defaultOptions}
      placeholder={placeholder}
      value={value}
      name={field.name}
      isDisabled={form.isSubmitting || disabled}
      onBlur={handleBlur}
      onInputChange={onInputChange}
      onChange={handleChange}
      onFocus={onFocusHandler}
      className={selectClassName}
      classNamePrefix="react-select"
      theme={THEME_CONFIG}
    />
  );

  const errorLabel = (form.submitCount > 0 || touched) && error && (
    <label className="form-input-field__label error-label">{error?.value || error}</label>
  );

  return (
    <Row>
      <Col>
        <FormGroup className="form-group--bordered-label">
          <Label
            className={`form-input-field-label ${required ? 'label-required' : ''} ${
              disabled ? 'form-input-field-label__disabled' : ''
            }`}
          >
            {label}
          </Label>
          {selectElement}
          {errorLabel}
        </FormGroup>
      </Col>
    </Row>
  );
}

function FormAsyncSelectFieldBordered(props) {
  return <Field {...props} component={FormAsyncSelectFieldBorderedInner} />;
}

FormAsyncSelectFieldBordered.defaultProps = {
  label: '',
  placeholder: '',
  options: [],
  onChangeHandler: () => {},
  loadRightAway: true,
  invalid: '',
  disabled: false,
  required: false,
};

FormAsyncSelectFieldBordered.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  onChangeHandler: PropTypes.func,
  loadRightAway: PropTypes.bool,
  invalid: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

export default FormAsyncSelectFieldBordered;
